import { useTranslation } from 'react-i18next';

export function Footer() {
  const { t, i18n } = useTranslation('application');
  const isEnglish = i18n.language === 'en';
  const logoImageSrc = t('header.logoImageSrc');
  const logoAlt = t('header.logoAlt');
  const ciwfLogoUrl = t('footer.logos.ciwfLogoUrl');

  // Helper function to check if a translation key should be shown
  // Returns true only if the value exists, is not empty, and is not a fallback from English
  const shouldShowLink = (key: string, urlKey: string): boolean => {
    const currentLang = i18n.language;
    
    // For non-English languages, check the resource bundle directly to see if keys exist
    // and are not empty strings (without triggering fallback)
    if (!isEnglish) {
      // Get the resource bundle for the current language
      const resourceBundle = i18n.getResourceBundle(currentLang, 'application');
      
      if (!resourceBundle) {
        return false;
      }
      
      // Navigate through nested keys (e.g., 'footer.accessibility' -> resourceBundle.footer.accessibility)
      const keyParts = key.split('.');
      const urlKeyParts = urlKey.split('.');
      
      let value: string | undefined;
      let urlValue: string | undefined;
      
      // Get value by traversing nested object
      let current: unknown = resourceBundle;
      for (const part of keyParts) {
        if (current && typeof current === 'object' && current !== null && part in current) {
          current = (current as Record<string, unknown>)[part];
        } else {
          current = undefined;
          break;
        }
      }
      value = typeof current === 'string' ? current : undefined;
      
      // Get URL value by traversing nested object
      current = resourceBundle;
      for (const part of urlKeyParts) {
        if (current && typeof current === 'object' && current !== null && part in current) {
          current = (current as Record<string, unknown>)[part];
        } else {
          current = undefined;
          break;
        }
      }
      urlValue = typeof current === 'string' ? current : undefined;
      
      // If either value doesn't exist or is an empty string, don't show
      if (!value || value.trim() === '' || !urlValue || urlValue.trim() === '') {
        return false;
      }
    } else {
      // For English, use normal translation check
      const value = t(key);
      const url = t(urlKey);
      
      if (!value || !url || value.trim() === '' || url.trim() === '') {
        return false;
      }
    }
    
    return true;
  };

  return (
    <footer role="contentinfo" className="w-full bg-[#0a1402] py-12 mt-auto">
      <div className="max-w-[1800px] mx-auto px-8 lg:px-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mb-8">
          {/* Privacy Links */}
          <div className="space-y-2 text-center lg:text-left">
            {shouldShowLink('footer.privacyPolicy', 'footer.privacyPolicyUrl') && (
              <a 
                href={t('footer.privacyPolicyUrl')} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block text-white hover:text-white/80 underline transition-colors"
              >
                {t('footer.privacyPolicy')}
              </a>
            )}
            {shouldShowLink('footer.termsAndConditions', 'footer.termsAndConditionsUrl') && (
              <a 
                href={t('footer.termsAndConditionsUrl')} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block text-white hover:text-white/80 underline transition-colors"
              >
                {t('footer.termsAndConditions')}
              </a>
            )}
            {shouldShowLink('footer.accessibility', 'footer.accessibilityUrl') && (
              <a 
                href={t('footer.accessibilityUrl')} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="block text-white hover:text-white/80 underline transition-colors"
              >
                {t('footer.accessibility')}
              </a>
            )}
          </div>

          {/* Campaign Logos */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-6">
            <img 
              alt={logoAlt} 
              className="h-12 w-auto object-contain" 
              src={logoImageSrc}
            />
            {!isEnglish && (
              <img 
                alt={t('footer.logos.compassionInWorldFarming')} 
                className="h-12 w-auto object-contain" 
                src={ciwfLogoUrl}
              />
            )}
          </div>

          {/* Support Contact */}
          <div className="text-center lg:text-right">
            <p className="text-white mb-2">
              {t('footer.supportText')}{' '}
              <a 
                className="underline hover:text-white/80 transition-colors" 
                href={`mailto:${t('help.contact.email')}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('help.contact.email')}
              </a>
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center pt-8 border-t border-white/20">
          <p className="text-white">
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
}

